
import { Link } from "react-router-dom";
import ThemeToggle from "../theme/ThemeToggle";
import { useAuth } from "../auth/AuthProvider";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import i18n from "../../i18n";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

function getLangPath(path: string, lang: string) {
  if (lang === "fr") {
    if (path === "/") return "/fr";
    if (path.startsWith("/fr")) return path;
    return `/fr${path}`;
  } else {
    if (path.startsWith("/fr")) return path.replace(/^\/fr/, "") || "/";
    return path;
  }
}

export default function Navbar() {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const lang = i18n.language;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between">
        <div className="flex items-center gap-8">
          <Link to={getLangPath("/", lang)} className="text-2xl font-semibold">
            PierreFT
          </Link>
          
          <nav className="hidden md:flex items-center gap-6">
            <Link to={getLangPath("/", lang)} className="text-md font-medium transition-colors hover:text-primary">
              {t("home")}
            </Link>
            <Link to={getLangPath("/blog", lang)} className="text-md font-medium transition-colors hover:text-primary">
              {t("blog")}
            </Link>
            <Link to={getLangPath("/courses", lang)} className="text-md font-medium transition-colors hover:text-primary">
              {t("courses")}
            </Link>
          </nav>
        </div>

        <div className="flex items-center gap-4">
          <ThemeToggle />
          
          {user ? (
            <div className="hidden md:flex items-center gap-4">
              <Link to={getLangPath("/dashboard", lang)} className="text-md font-medium transition-colors hover:text-primary">
                {t("my_account")}
              </Link>
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-4">
              <Link 
                to={getLangPath("/auth", lang)} 
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/80 hover:text-primary-foreground"
              >
                {t("login")}
              </Link>
            </div>
          )}

          <button
            className="block md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
          <LanguageSwitcher />
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden border-t border-border/40 py-4">
          <nav className="container flex flex-col space-y-4">
            <Link 
              to={getLangPath("/", lang)} 
              className="text-md font-medium transition-colors hover:text-primary"
              onClick={() => setIsMenuOpen(false)}
            >
              {t("home")}
            </Link>
            <Link 
              to={getLangPath("/blog", lang)} 
              className="text-md font-medium transition-colors hover:text-primary"
              onClick={() => setIsMenuOpen(false)}
            >
              {t("blog")}
            </Link>
            <Link 
              to={getLangPath("/courses", lang)} 
              className="text-md font-medium transition-colors hover:text-primary"
              onClick={() => setIsMenuOpen(false)}
            >
              {t("courses")}
            </Link>
            {user ? (
              <>
                <Link 
                  to={getLangPath("/dashboard", lang)} 
                  className="text-md font-medium transition-colors hover:text-primary"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t("my_account")}
                </Link>
              </>
            ) : (
              <>
                <Link 
                  to={getLangPath("/auth", lang)} 
                  className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/80 hover:text-primary-foreground inline-block w-fit"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t("login")}
                </Link>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}

function LanguageSwitcher() {
  const [lang, setLang] = useState(i18n.language);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onLangChanged = (lng: string) => setLang(lng);
    i18n.on("languageChanged", onLangChanged);
    
    // For backward compatibility, check both storage methods
    const storedLang = localStorage.getItem("lang");
    if (storedLang && (storedLang === "en" || storedLang === "fr") && storedLang !== i18n.language) {
      i18n.changeLanguage(storedLang);
    }
    
    return () => { i18n.off("languageChanged", onLangChanged); };
  }, []);

  const setLanguage = (lng: "en" | "fr") => {
    // Update i18n language
    i18n.changeLanguage(lng);

    // Store in both the old and new storage locations
    // Old storage (for backward compatibility)
    localStorage.setItem("lang", lng);

    // New storage with user preference flag
    try {
      localStorage.setItem("language_preference", JSON.stringify({
        language: lng,
        isUserChoice: true,  // Mark as explicit user choice
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error("Error storing language preference:", error);
    }

    // Navigate to equivalent path in target language
    const currentPath = location.pathname;
    const newPath = getLangPath(currentPath, lng);
    navigate(newPath);

    // Update local state
    setLang(lng);
  };

  return (
    <div className="flex items-center gap-1 border rounded px-1 py-0.5 bg-muted/50">
      <button
        className={`px-2 py-1 rounded text-xs font-medium transition-colors ${lang === "en" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
        onClick={() => setLanguage("en")}
        aria-pressed={lang === "en"}
      >EN</button>
      <button
        className={`px-2 py-1 rounded text-xs font-medium transition-colors ${lang === "fr" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
        onClick={() => setLanguage("fr")}
        aria-pressed={lang === "fr"}
      >FR</button>
    </div>
  );
}
